import React, { useState, useEffect } from 'react';
import { eventAPI } from '../../../api';
import './EventForm.css';

const EventForm = ({ event, onClose, onSubmit }) => {
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        location: '',
        start_date: '',
        end_date: '',
        max_participants: '',
        price: '',
        meet_link: '',
        is_active: true,
        image: null
    });
    
    const [imagePreview, setImagePreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (event) {
            setFormData({
                title: event.title || '',
                description: event.description || '',
                location: event.location || '',
                start_date: event.start_date ? formatDateForInput(event.start_date) : '',
                end_date: event.end_date ? formatDateForInput(event.end_date) : '',
                max_participants: event.max_participants || '',
                price: event.price || '0.00',
                meet_link: event.meet_link || '',
                is_active: event.is_active ?? true,
                image: null
            });
            
            if (event.image) {
                setImagePreview(`http://127.0.0.1:8000/storage/${event.image}`);
            }
        }
    }, [event]);

    // Fonction pour formater la date pour l'input datetime-local
    const formatDateForInput = (dateString) => {
        const date = new Date(dateString);
        return date.toISOString().slice(0, 16);
    };

    const handleChange = (e) => {
        const { name, value, type, checked, files } = e.target;
        
        if (type === 'file') {
            const file = files[0];
            setFormData(prev => ({ ...prev, [name]: file }));
            
            if (file) {
                const reader = new FileReader();
                reader.onloadend = () => {
                    setImagePreview(reader.result);
                };
                reader.readAsDataURL(file);
            } else {
                setImagePreview(event?.image ? `http://127.0.0.1:8000/storage/${event.image}` : null);
            }
        } else if (type === 'checkbox') {
            setFormData(prev => ({ ...prev, [name]: checked }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
        
        // Clear error for this field
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const validateForm = () => {
        const newErrors = {};
        
        if (!formData.title.trim()) newErrors.title = 'Le titre est requis';
        if (!formData.description.trim()) newErrors.description = 'La description est requise';
        if (!formData.location.trim()) newErrors.location = 'Le lieu est requis';
        if (!formData.start_date) newErrors.start_date = 'La date de début est requise';
        if (!formData.end_date) newErrors.end_date = 'La date de fin est requise';
        
        if (formData.start_date && formData.end_date) {
            const start = new Date(formData.start_date);
            const end = new Date(formData.end_date);
            if (end <= start) {
                newErrors.end_date = 'La date de fin doit être après la date de début';
            }
        }
        
        if (formData.max_participants && formData.max_participants < 1) {
            newErrors.max_participants = 'Le nombre de participants doit être au moins 1';
        }
        
        if (formData.price && formData.price < 0) {
            newErrors.price = 'Le prix ne peut pas être négatif';
        }
        
        if (formData.meet_link && !formData.meet_link.match(/^(https?:\/\/)/)) {
            newErrors.meet_link = "L'URL n'est pas valide";
        }
        
        return newErrors;
    };

const handleSubmit = async (e) => {
    e.preventDefault();
    
    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
        setErrors(formErrors);
        return;
    }
    
    setLoading(true);
    
    try {
        // Create FormData for file upload
        const submitData = new FormData();
        
        // Append all form fields with proper formatting
        Object.keys(formData).forEach(key => {
            if (formData[key] !== null && formData[key] !== undefined && formData[key] !== '') {
                if (key === 'image' && formData[key] instanceof File) {
                    submitData.append(key, formData[key]);
                } else if (key === 'price') {
                    // Ensure price is sent as decimal
                    submitData.append(key, parseFloat(formData[key]) || 0);
                } else if (key === 'max_participants') {
                    // Only send if not empty
                    if (formData[key]) {
                        submitData.append(key, parseInt(formData[key]));
                    }
                } else if (key === 'is_active') {
                    // CORRECTION ICI : Envoyer comme booléen
                    submitData.append(key, formData[key] ? '1' : '0');
                    // Ou selon ce qu'attend Laravel :
                    // submitData.append(key, formData[key].toString());
                } else if (key !== 'image') {
                    submitData.append(key, formData[key]);
                }
            }
        });
        
        // Log FormData contents for debugging
        console.log('FormData contents:');
        for (let pair of submitData.entries()) {
            console.log(pair[0] + ': ' + pair[1]);
        }
        
        let response;
        if (event) {
            response = await eventAPI.update(event.id, submitData);
        } else {
            response = await eventAPI.create(submitData);
        }
        
        if (response.success) {
            onSubmit();
            alert(event ? 'Événement mis à jour avec succès' : 'Événement créé avec succès');
        }
    } catch (error) {
        console.error('Error saving event:', error);
        
        // Handle validation errors from API
        if (error.errors) {
            console.log('API Validation errors:', error.errors);
            setErrors(error.errors);
            
            // Show first error as alert
            const firstErrorKey = Object.keys(error.errors)[0];
            if (firstErrorKey && error.errors[firstErrorKey]) {
                alert(`Erreur: ${error.errors[firstErrorKey][0]}`);
            }
        } else {
            alert(error.message || 'Une erreur est survenue lors de l\'enregistrement');
        }
    } finally {
        setLoading(false);
    }
};
    const handleRemoveImage = () => {
        setFormData(prev => ({ ...prev, image: null }));
        setImagePreview(null);
        
        // If editing and image exists, we need to send a signal to remove it
        if (event?.image) {
            // Add a flag to indicate image removal
            setFormData(prev => ({ ...prev, remove_image: true }));
        }
    };

    return (
        <div className="event-form-container">
            <div className="form-header">
                <h3>{event ? 'Modifier l\'événement' : 'Créer un nouvel événement'}</h3>
                <button onClick={onClose} className="btn-close-form">
                    <i className="fas fa-times"></i>
                </button>
            </div>
            
            <form onSubmit={handleSubmit} className="event-form">
                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="title">Titre *</label>
                        <input
                            type="text"
                            id="title"
                            name="title"
                            value={formData.title}
                            onChange={handleChange}
                            placeholder="Ex: Conférence sur le développement web"
                            className={errors.title ? 'error' : ''}
                        />
                        {errors.title && <span className="error-message">{Array.isArray(errors.title) ? errors.title[0] : errors.title}</span>}
                    </div>
                    
                    <div className="form-group">
                        <label htmlFor="location">Lieu *</label>
                        <input
                            type="text"
                            id="location"
                            name="location"
                            value={formData.location}
                            onChange={handleChange}
                            placeholder="Ex: Paris, France"
                            className={errors.location ? 'error' : ''}
                        />
                        {errors.location && <span className="error-message">{Array.isArray(errors.location) ? errors.location[0] : errors.location}</span>}
                    </div>
                </div>
                
                <div className="form-group">
                    <label htmlFor="description">Description *</label>
                    <textarea
                        id="description"
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        rows="4"
                        placeholder="Décrivez votre événement..."
                        className={errors.description ? 'error' : ''}
                    />
                    {errors.description && <span className="error-message">{Array.isArray(errors.description) ? errors.description[0] : errors.description}</span>}
                </div>
                
                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="start_date">Date de début *</label>
                        <input
                            type="datetime-local"
                            id="start_date"
                            name="start_date"
                            value={formData.start_date}
                            onChange={handleChange}
                            className={errors.start_date ? 'error' : ''}
                        />
                        {errors.start_date && <span className="error-message">{Array.isArray(errors.start_date) ? errors.start_date[0] : errors.start_date}</span>}
                    </div>
                    
                    <div className="form-group">
                        <label htmlFor="end_date">Date de fin *</label>
                        <input
                            type="datetime-local"
                            id="end_date"
                            name="end_date"
                            value={formData.end_date}
                            onChange={handleChange}
                            className={errors.end_date ? 'error' : ''}
                        />
                        {errors.end_date && <span className="error-message">{Array.isArray(errors.end_date) ? errors.end_date[0] : errors.end_date}</span>}
                    </div>
                </div>
                
                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="max_participants">Nombre maximum de participants</label>
                        <input
                            type="number"
                            id="max_participants"
                            name="max_participants"
                            value={formData.max_participants}
                            onChange={handleChange}
                            min="1"
                            placeholder="Illimité si vide"
                            className={errors.max_participants ? 'error' : ''}
                        />
                        {errors.max_participants && <span className="error-message">{Array.isArray(errors.max_participants) ? errors.max_participants[0] : errors.max_participants}</span>}
                    </div>
                    
                    <div className="form-group">
                        <label htmlFor="price">Prix (€)</label>
                        <input
                            type="number"
                            id="price"
                            name="price"
                            value={formData.price}
                            onChange={handleChange}
                            min="0"
                            step="0.01"
                            placeholder="0.00"
                            className={errors.price ? 'error' : ''}
                        />
                        {errors.price && <span className="error-message">{Array.isArray(errors.price) ? errors.price[0] : errors.price}</span>}
                    </div>
                </div>
                
                <div className="form-group">
                    <label htmlFor="meet_link">Lien de réunion (URL)</label>
                    <input
                        type="url"
                        id="meet_link"
                        name="meet_link"
                        value={formData.meet_link}
                        onChange={handleChange}
                        placeholder="https://meet.google.com/..."
                        className={errors.meet_link ? 'error' : ''}
                    />
                    {errors.meet_link && <span className="error-message">{Array.isArray(errors.meet_link) ? errors.meet_link[0] : errors.meet_link}</span>}
                </div>
                
                <div className="form-group">
                    <label htmlFor="image">Image de l'événement</label>
                    <div className="image-upload-section">
                        <div className="image-preview-container">
                            {imagePreview ? (
                                <div className="image-preview">
                                    <img src={imagePreview} alt="Preview" />
                                    <button 
                                        type="button" 
                                        onClick={handleRemoveImage}
                                        className="btn-remove-image"
                                    >
                                        <i className="fas fa-times"></i>
                                    </button>
                                </div>
                            ) : (
                                <div className="image-placeholder">
                                    <i className="fas fa-image"></i>
                                    <p>Aucune image sélectionnée</p>
                                </div>
                            )}
                        </div>
                        
                        <div className="file-input-container">
                            <input
                                type="file"
                                id="image"
                                name="image"
                                onChange={handleChange}
                                accept="image/*"
                                className="file-input"
                            />
                            <label htmlFor="image" className="file-input-label">
                                <i className="fas fa-cloud-upload-alt"></i>
                                Choisir une image
                            </label>
                            <p className="file-hint">JPEG, PNG, GIF max 2MB</p>
                        </div>
                    </div>
                </div>
                
                <div className="form-group checkbox-group">
                    <label className="checkbox-label">
                        <input
                            type="checkbox"
                            name="is_active"
                            checked={formData.is_active}
                            onChange={handleChange}
                        />
                        <span className="checkbox-custom"></span>
                        Événement actif
                    </label>
                    <p className="checkbox-help">
                        Un événement inactif ne sera pas visible sur le site public
                    </p>
                </div>
                
                <div className="form-actions">
                    <button 
                        type="button" 
                        onClick={onClose}
                        className="btn-cancel"
                        disabled={loading}
                    >
                        Annuler
                    </button>
                    <button 
                        type="submit" 
                        className="btn-submit"
                        disabled={loading}
                    >
                        {loading ? (
                            <>
                                <i className="fas fa-spinner fa-spin"></i>
                                Enregistrement...
                            </>
                        ) : event ? (
                            'Mettre à jour'
                        ) : (
                            'Créer l\'événement'
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default EventForm;